import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Grid,
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Badge as BadgeCheckIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AddDesignation from './AddDesignation';

// Mock data
const businessOptions = [
  { id: 1, name: 'FullSolution Technologies' },
  { id: 2, name: 'FullSolution Retail Solutions' },
  { id: 3, name: 'FullSolution Consulting' },
];

const branchOptions = [
  { id: 1, businessId: 1, name: 'Head Office' },
  { id: 2, businessId: 2, name: 'Mumbai Branch' },
  { id: 3, businessId: 3, name: 'Delhi Branch' },
];

const departments = [
  { id: 1, businessId: 1, branchId: 1, name: 'Human Resources' },
  { id: 2, businessId: 2, branchId: 2, name: 'Finance' },
  { id: 3, businessId: 3, branchId: 3, name: 'IT' },
];

const DesignationSetup = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [newDesignation, setNewDesignation] = useState({
    businessId: '',
    branchId: '',
    departmentId: '',
    designationName: '',
    description: '',
    status: 'active',
  });

  // Mock designation data
  const [designations, setDesignations] = useState([
    {
      id: 1,
      businessId: 1,
      branchId: 1,
      departmentId: 1,
      designationName: 'HR Manager',
      description: 'Manages HR operations and team.',
      status: 'active',
      createdDate: '2020-01-15',
    },
    {
      id: 2,
      businessId: 2,
      branchId: 2,
      departmentId: 2,
      designationName: 'Finance Executive',
      description: 'Handles financial records and reporting.',
      status: 'active',
      createdDate: '2021-06-20',
    },
    {
      id: 3,
      businessId: 3,
      branchId: 3,
      departmentId: 3,
      designationName: 'IT Support',
      description: 'Provides IT support and troubleshooting.',
      status: 'inactive',
      createdDate: '2022-03-10',
    },
  ]);

  // Filter designations
  const filteredDesignations = designations.filter((desig) => {
    const matchesSearch = desig.designationName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || desig.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const paginatedDesignations = filteredDesignations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
  };

  const handleOpenModal = () => {
    setNewDesignation({
      businessId: '',
      branchId: '',
      departmentId: '',
      designationName: '',
      description: '',
      status: 'active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDesignation(null);
  };

  const handleView = (designation) => {
    setSelectedDesignation(designation);
    setOpenViewModal(true);
  };

  const handleEdit = (designation) => {
    setSelectedDesignation(designation);
    setNewDesignation(designation);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this designation?')) {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setDesignations(designations.filter((desig) => desig.id !== id));
        setSuccessMessage('Designation deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        setErrors({ submit: 'Failed to delete designation' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDesignationAdded = (newDesig) => {
    setDesignations([...designations, newDesig]);
    setSuccessMessage('Designation added successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSaveDesignation = async () => {
    const validationErrors = {};
    if (!newDesignation.businessId) validationErrors.businessId = 'Business is required';
    if (!newDesignation.branchId) validationErrors.branchId = 'Branch is required';
    if (!newDesignation.departmentId) validationErrors.departmentId = 'Department is required';
    if (!newDesignation.designationName) validationErrors.designationName = 'Designation name is required';

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (selectedDesignation) {
        // Update existing
        setDesignations(designations.map((desig) =>
          desig.id === selectedDesignation.id ? { ...desig, ...newDesignation } : desig
        ));
        setSuccessMessage('Designation updated successfully!');
      } else {
        // Add new
        const newDesig = {
          ...newDesignation,
          id: Date.now(),
          createdDate: new Date().toISOString().split('T')[0],
        };
        setDesignations([...designations, newDesig]);
        setSuccessMessage('Designation added successfully!');
      }

      handleCloseModal();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrors({ submit: 'Failed to save designation' });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    return status === 'active'
      ? { backgroundColor: '#e8f5e9', color: '#2e7d32' }
      : { backgroundColor: '#ffebee', color: '#c62828' };
  };

  // Filter branches and departments
  const filteredBranches = branchOptions.filter(b => b.businessId === newDesignation.businessId);
  const filteredDepartments = departments.filter(d =>
    d.businessId === newDesignation.businessId && d.branchId === newDesignation.branchId
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BadgeCheckIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Designation Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage organization designations
            </Typography>
          </Box>
        </Box>
        <AddDesignation onDesignationAdded={handleDesignationAdded} />
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search designations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ textTransform: 'none' }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Designation Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDesignations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No designations found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedDesignations.map((desig) => {
                  const business = businessOptions.find(b => b.id === desig.businessId);
                  const department = departments.find(d => d.id === desig.departmentId);
                  return (
                    <TableRow key={desig.id} hover>
                      <TableCell>{desig.designationName}</TableCell>
                      <TableCell>{department ? department.name : '-'}</TableCell>
                      <TableCell>{business ? business.name : '-'}</TableCell>
                      <TableCell>{desig.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={desig.status}
                          size="small"
                          sx={getStatusStyle(desig.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(desig)}>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(desig)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(desig.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredDesignations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}

        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {selectedDesignation ? 'Edit Designation' : 'Add Designation'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.businessId)}>
                <InputLabel>Business *</InputLabel>
                <Select
                  value={newDesignation.businessId}
                  onChange={(e) => setNewDesignation({ ...newDesignation, businessId: e.target.value, branchId: '', departmentId: '' })}
                  label="Business *"
                >
                  {businessOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.branchId)} disabled={!newDesignation.businessId}>
                <InputLabel>Branch *</InputLabel>
                <Select
                  value={newDesignation.branchId}
                  onChange={(e) => setNewDesignation({ ...newDesignation, branchId: e.target.value, departmentId: '' })}
                  label="Branch *"
                >
                  {filteredBranches.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={Boolean(errors.departmentId)} disabled={!newDesignation.branchId}>
                <InputLabel>Department *</InputLabel>
                <Select
                  value={newDesignation.departmentId}
                  onChange={(e) => setNewDesignation({ ...newDesignation, departmentId: e.target.value })}
                  label="Department *"
                >
                  {filteredDepartments.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Designation Name *"
                value={newDesignation.designationName}
                onChange={(e) => setNewDesignation({ ...newDesignation, designationName: e.target.value })}
                error={Boolean(errors.designationName)}
                helperText={errors.designationName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newDesignation.description}
                onChange={(e) => setNewDesignation({ ...newDesignation, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newDesignation.status}
                  onChange={(e) => setNewDesignation({ ...newDesignation, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDesignation}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Designation Details</DialogTitle>
        <DialogContent>
          {selectedDesignation && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Designation Name:</strong> {selectedDesignation.designationName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Business:</strong>{' '}
                {businessOptions.find(b => b.id === selectedDesignation.businessId)?.name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Department:</strong>{' '}
                {departments.find(d => d.id === selectedDesignation.departmentId)?.name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Description:</strong> {selectedDesignation.description}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Status:</strong>{' '}
                <Chip label={selectedDesignation.status} size="small" sx={getStatusStyle(selectedDesignation.status)} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Created Date:</strong> {selectedDesignation.createdDate}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesignationSetup;
